import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  useTheme,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import TradeCalendar from './TradeCalendar';
import TradeFilters from '../components/common/TradeFilters';
import useTradeFilters from '../hooks/useTradeFilters';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

const CalendarPage = () => {
  const theme = useTheme();
  const [trades, setTrades] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [strategyNamesMap, setStrategyNamesMap] = useState({});
  
  const {
    selectedCategory, selectedSymbol, selectedStrategy, selectedStatus, selectedTags, startDate, endDate,
    setSelectedCategory, setSelectedSymbol, setSelectedStrategy, setSelectedStatus, setSelectedTags, setStartDate, setEndDate,
    filteredTrades, symbols, statuses, allTags, handleTimeFilter
  } = useTradeFilters(trades);

  useEffect(() => {
    fetchTrades();
    fetchStrategies();
    
    // Listen for trade creation events
    const handleTradeCreated = () => {
      fetchTrades();
    };
    
    window.addEventListener('tradeCreated', handleTradeCreated);
    
    return () => {
      window.removeEventListener('tradeCreated', handleTradeCreated);
    };
  }, []);

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(response.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    }
  };

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const strategyMap = {};
      for (const strat of response.data) {
        strategyMap[strat.strategyId] = strat.name;
      }
      setStrategies(response.data.map(s => s.strategyId));
      setStrategyNamesMap(strategyMap);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  // Get trades for the current week
  const startOfWeek = dayjs().startOf('week');
  const endOfWeek = dayjs().endOf('week');
  
  let weeklyTrades = filteredTrades.filter(t => {
    if (!t.tradeDate) return false;
    const tradeDate = dayjs(t.tradeDate);
    return tradeDate.isAfter(startOfWeek) && tradeDate.isBefore(endOfWeek);
  });
  
  // If no trades this week, show all trades
  if (weeklyTrades.length === 0) {
    weeklyTrades = filteredTrades;
  }
  
  // Calculate weekly stats
  const weeklyStats = {
    totalTrades: weeklyTrades.length,
    totalPnL: weeklyTrades.reduce((sum, t) => sum + parseFloat(t.profitLoss || 0), 0),
    wins: weeklyTrades.filter(t => t.status === 'WIN').length,
    losses: weeklyTrades.filter(t => t.status === 'LOSS').length
  };
  
  // Calculate win rate
  const winRate = weeklyStats.wins + weeklyStats.losses > 0 
    ? ((weeklyStats.wins / (weeklyStats.wins + weeklyStats.losses)) * 100).toFixed(1) + '%' 
    : '0%';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
        Trade Calendar
      </Typography>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <TradeFilters
          selectedCategory={selectedCategory}
          selectedSymbol={selectedSymbol}
          selectedStrategy={selectedStrategy}
          selectedStatus={selectedStatus}
          selectedTags={selectedTags}
          startDate={startDate}
          endDate={endDate}
          onCategoryChange={setSelectedCategory}
          onSymbolChange={setSelectedSymbol}
          onStrategyChange={setSelectedStrategy}
          onStatusChange={setSelectedStatus}
          onTagsChange={setSelectedTags}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onTimeFilter={handleTimeFilter}
          symbols={symbols}
          strategies={strategies}
          statuses={statuses}
          allTags={allTags}
          strategyNamesMap={strategyNamesMap}
        />
      </Paper>

      {/* Weekly Summary */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
        {weeklyTrades.length === filteredTrades.length ? '📊 Trading Overview' : '📊 Weekly Overview'}
      </Typography>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card 
              elevation={1} 
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 2,
                textAlign: 'center',
                p: 2
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Total Trades
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {weeklyStats.totalTrades}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              elevation={1} 
              sx={{ 
                background: weeklyStats.totalPnL >= 0 
                  ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' 
                  : 'linear-gradient(135deg, #fc466b 0%, #3f5efb 100%)',
                color: 'white',
                borderRadius: 2,
                textAlign: 'center',
                p: 2
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  {weeklyTrades.length === filteredTrades.length ? 'Total P&L' : 'Weekly P&L'}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${weeklyStats.totalPnL.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card 
              elevation={1} 
              sx={{ 
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                color: '#8b4513',
                borderRadius: 2,
                textAlign: 'center',
                p: 2
              }}
            >
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                  Win Rate
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {winRate}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Calendar */}
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
        📅 Calendar View
      </Typography>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <TradeCalendar trades={filteredTrades} />
      </Paper>
    </Box>
  );
};

export default CalendarPage;